import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  NgZone,
  Output,
  PLATFORM_ID,
  TransferState,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  DxHtmlEditorComponent,
  DxTemplateHost,
  IterableDifferHelper,
  NestedOptionHost,
  WatcherHelper,
} from 'devextreme-angular';
import Quill from 'devextreme-quill';
import { MessageBoxService } from 'libs/abc/messages/src/lib/messageBox/messageBox.service';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxIcon,
  MessageMode,
} from 'libs/abc/messages/src/lib/messageBox/messages.model';
import { MessageSettings } from 'libs/abc/messages/src/lib/messageBox/messageSettings';

import { ResourceManager } from '../../../../../../core/resources/src';
import { AbcContainerService } from '../../../base/src/abcContainer.service';
import { AbcPopupComponent } from '../abc-popup/abc-popup.component';
import { AbcCoreComponentService } from '../abcCoreComponent.service';
import { AbcCoreContainerService } from '../abcCoreContainer.service';

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @angular-eslint/no-output-on-prefix */
@Component({
  selector: 'abc-html-editor',
  template: ``,
  providers: [
    DxTemplateHost,
    WatcherHelper,
    IterableDifferHelper,
    NestedOptionHost,
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => AbcHtmlEditorComponent),
    },
  ],
  imports: [],
  styleUrls: ['./abc-html-editor.component.scss'],
})
export class AbcHtmlEditorComponent extends DxHtmlEditorComponent implements AfterViewInit, AfterViewChecked {
  private resourceManager = ResourceManager.getResources('abc-messages-messageBox');

  @Input() label: string;
  @Input() showIconEdit = true;
  @Input() showToolbar = false;
  @Input() displayEdit = false;
  @Input() popupEditorWidth = '700px';
  @Input() popupEditorHeight = '400px';
  @Input() popupEditorResizeEnabled = true;
  @Input() enableConfirmationWindow = true;
  @Input() showSaveButton = false;
  @Input() onCustomSaveAction: (popupInstance: AbcPopupComponent) => void;
  @Input() showCloseButtonOption = false;
  @Output() onSave: EventEmitter<any> = new EventEmitter();
  @Output() onHidingPopUp: EventEmitter<any> = new EventEmitter();

  public abcPopup: AbcPopupComponent;

  private elementRef: ElementRef;
  private htmlEditorComponent: DxHtmlEditorComponent;
  private div: HTMLDivElement;
  private span: HTMLSpanElement;

  constructor(
    elementRef: ElementRef,
    ngZone: NgZone,
    templateHost: DxTemplateHost,
    _watcherHelp: WatcherHelper,
    _idh: IterableDifferHelper,
    optionHost: NestedOptionHost,
    transferState: TransferState,
    @Inject(PLATFORM_ID) platformId: any,
    private abcCoreComponentService: AbcCoreComponentService,
    public abcContainer: AbcContainerService,
    public abcCoreContainer: AbcCoreContainerService,
    private messageBoxService: MessageBoxService,
  ) {
    super(elementRef, ngZone, templateHost, _watcherHelp, _idh, optionHost, transferState, platformId);
    this.elementRef = elementRef;
    this.readOnly = this.readOnly || false;
    this.stylingMode = this.stylingMode || this.abcCoreComponentService.stylingMode;
  }

  override ngAfterViewInit() {
    this.createLabel();
    this.createIconEdit();

    if (this.showIconEdit) {
      this.div.addEventListener('click', () => {
        this.onTextEdit();
      });
      this.triggerMouseEvents(this.elementRef);
    }

    if (this.showToolbar) {
      this.initializeToolbar(this);
    }

    this.instance.registerKeyHandler('del', () => {
      this.value = null;
      this.onSave.emit(this.value);
    });

    const content = this.extractContentAndApplyStyles(this.value);
    const classNames = this.extractClassNames(content);
    this.registerClassesAsBlots(classNames);

    this.instance.endUpdate();
  }

  public getPlainText(): string {
    const divElement: HTMLDivElement = document.createElement('div');
    divElement.innerHTML = this.value;
    return divElement.textContent;
  }

  private createLabel() {
    if (this.label) {
      const span = document.createElement('p');
      span.innerText = this.label;
      span.classList.add(
        this.stylingMode === 'filled'
          ? this.rtlEnabled
            ? 'labelHtmlEditorFilledRtl'
            : 'labelHtmlEditorFilled'
          : this.rtlEnabled
            ? 'labelHtmlEditorRtl'
            : 'labelHtmlEditor',
      );
      this.elementRef.nativeElement.insertAdjacentElement('beforebegin', span);
    }
  }

  private createIconEdit() {
    if (this.showIconEdit) {
      this.span = document.createElement('span');
      this.span.className = 'dx-icon-edit';
      this.span.style.display = 'none';
      this.div = document.createElement('div');
      this.div.style.padding = '6px';
      this.div.style.position = 'absolute';
      this.div.style.right = '0px';
      this.div.style.zIndex = '1';
      this.div.style.cursor = 'pointer';
      this.div.appendChild(this.span);
      this.elementRef.nativeElement.parentElement.style.position = 'relative';
      setTimeout(() => {
        this.elementRef.nativeElement.firstChild?.append(this.div);
      }, 0);
    }
  }

  private onTextEdit() {
    const popup = this.abcCoreContainer.createComponent(null, AbcPopupComponent);
    this.abcPopup = <AbcPopupComponent>popup.componentRef.instance;
    this.abcPopup.title = 'Editor';
    this.abcPopup.component = DxHtmlEditorComponent;
    this.abcPopup.resizeEnabled = this.popupEditorResizeEnabled;
    this.abcPopup.width = this.popupEditorWidth;
    this.abcPopup.height = this.popupEditorHeight;
    if (this.showCloseButtonOption) {
      this.abcPopup.showCloseButton = true;
    }
    const componentInstance = this.abcPopup.openPopUp();
    this.htmlEditorComponent = <DxHtmlEditorComponent>componentInstance.instance;
    this.abcPopup.onShown.subscribe(() => {
      this.initializeToolbar(this.htmlEditorComponent);

      if (this.showSaveButton) {
        this.htmlEditorComponent.toolbar.items.unshift({
          name: 'save',
          options: {
            text: 'Save',
            onClick: () => {
              if (!this.isEqual(this.htmlEditorComponent.value, this.value)) {
                this.value = this.htmlEditorComponent.value;
                this.onSave.emit(this.value);
              }
              if (this.onCustomSaveAction) {
                this.onCustomSaveAction(this.abcPopup);
              }
            },
          },
        });
      }
      this.htmlEditorComponent.value = this.value;
      this.htmlEditorComponent.height = '100%';
      this.htmlEditorComponent.focusStateEnabled = false;
    });

    this.abcPopup.onHiding.subscribe((e) => {
      this.onHidingPopUp.emit(e);
      if (this.enableConfirmationWindow) {
        if (!this.isEqual(this.htmlEditorComponent.value, this.value)) {
          (e as any).cancel = true;
          const settings = new MessageSettings();
          settings.mode = MessageMode.Question;
          settings.icon = MessageBoxIcon.Question;
          settings.button = MessageBoxButtons.YesNoCancel;
          settings.message = this.resourceManager('Save');
          this.messageBoxService.showMessageBox(settings).subscribe((result: DialogResult) => {
            if (DialogResult.No === result['result']) {
              this.abcPopup.instance.dispose();
            } else if (DialogResult.Yes === result['result']) {
              this.value = this.htmlEditorComponent.value;
              this.onSave.emit(this.value);
              this.abcPopup.instance.dispose();
            } else if (DialogResult.Cancel === result['result']) {
              return;
            }
            this.messageBoxService.dialog.closeAll();
          });
        }
      } else {
        this.value = this.htmlEditorComponent.value;
      }
    });
  }

  private initializeToolbar(htmlEditor: AbcHtmlEditorComponent | DxHtmlEditorComponent): void {
    htmlEditor.toolbar = {
      items: [
        'undo',
        'redo',
        'separator',
        {
          name: 'size',
          acceptedValues: ['8pt', '10pt', '12pt', '14pt', '18pt', '24pt', '36pt'],
        },
        {
          name: 'font',
          acceptedValues: [
            'Arial',
            'Courier New',
            'Georgia',
            'Impact',
            'Lucida Console',
            'Tahoma',
            'Times New Roman',
            'Verdana',
          ],
        },
        'separator',
        'bold',
        'italic',
        'strike',
        'underline',
        'separator',
        'alignLeft',
        'alignCenter',
        'alignRight',
        'alignJustify',
        'separator',
        'orderedList',
        'bulletList',
        'separator',
        {
          name: 'header',
          acceptedValues: [1, 2, 3, false],
        },
        'separator',
        'color',
        'background',
        'separator',
        'link',
        'image',
        'separator',
        'clear',
        'codeBlock',
        'blockquote',
      ],
    };
  }
  private triggerMouseEvents(elementRef) {
    if (this.showIconEdit) {
      const handleMouseOver = () => {
        this.span.style.display = 'block';
        this.displayEdit = true;
      };
      const handleMouseLeave = () => {
        this.span.style.display = 'none';
        this.displayEdit = false;
      };
      elementRef.nativeElement.addEventListener('mouseover', handleMouseOver);
      elementRef.nativeElement.addEventListener('mouseleave', handleMouseLeave);
      this.div.addEventListener('mouseover', handleMouseOver);
      this.div.addEventListener('mouseleave', handleMouseLeave);
    }
  }

  private isEqual(str1: string, str2: string): boolean {
    let result;
    if (str1?.length >= 0 && str2?.length >= 0) {
      const countChars = (str: string) => {
        const counts = { space: 0, symbol: 0 };
        for (let i = 0; i < str.length; i++) {
          const char = str[i];
          if (char === ' ') {
            counts.space++;
          } else if (!/^[a-zA-Z0-9]+$/.test(char)) {
            counts.symbol++;
          }
        }
        return str
          .replace(/[^a-zA-Z0-9]/g, '')
          .split('')
          .reduce((acc, char) => {
            acc[char] = (acc[char] || 0) + 1;
            return acc;
          }, counts);
      };
      const count1 = countChars(str1);
      const count2 = countChars(str2);
      result = JSON.stringify(count1) === JSON.stringify(count2);
    } else {
      result = false;
    }
    return result;
  }

  /**
   * extracts body tag content from the editor's value and returns it, apply styles to the current window instance
   * @param value
   * @returns body tag content from the editor's value: string
   */
  public extractContentAndApplyStyles(value: string): string {
    if (!value) {
      return '';
    }

    // Grab <style> block if present
    const cleanValue = value.replace(/\\"/g, '');
    const styleMatch = cleanValue.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
    const styles = styleMatch ? `${styleMatch[1]}` : '';

    // Grab body content
    const bodyMatch = cleanValue.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    const body = bodyMatch ? bodyMatch[1] : cleanValue;

    const stylesTag = document.createElement('style');
    stylesTag.type = 'text/css';
    stylesTag.appendChild(document.createTextNode(styles));
    document.head.append(stylesTag);

    return body;
  }

  /**
   * extracts classNames from given html content
   * @param htmlContent
   * @returns html content classNames array: Array<{ tagName: string; className: string }>
   */
  public extractClassNames(htmlContent: string): Array<{ tagName: string; className: string }> {
    const host = document.createElement('div');
    host.innerHTML = htmlContent;

    const classes = new Array<{ tagName: string; className: string }>();

    host
      .querySelectorAll('[class]')
      .forEach((element) =>
        element.className
          .split(/\s+/)
          .forEach((className) => className && classes.push({ tagName: element.tagName, className: className })),
      );

    if (!classes.entries()) {
      return;
    }

    return classes;
  }

  /**
   * takes classNames array and registers custom Blots for the quill
   * editor used by the html-editor component to preserve original formats
   * @param classNames
   */
  private registerClassesAsBlots(classNames: Array<{ tagName: string; className: string }>): void {
    const Inline = Quill.import('blots/inline');
    const url = window.location.href?.split('#')[0];

    for (const entry of classNames) {
      const blotKey = entry.className.replace(/[^a-zA-Z0-9]/g, '_');

      class CustomBlot extends Inline {
        static blotName = entry.tagName === 'A' ? 'link' : blotKey;
        static tagName = entry.tagName;
        static className = entry.className;

        static create(value: any) {
          const node = super.create();

          if (entry.className) {
            node.classList.add(entry.className);
          }

          if (value?.id) {
            node.setAttribute('id', value.id);
          }

          if (entry.tagName === 'A') {
            if (value?.href) {
              const fullHref = value.href.includes('#') ? url + value.href : value.href;
              node.setAttribute('href', fullHref);
              if (!value.href.includes('#')) {
                node.setAttribute('target', '_blank');
              }
            }
          }

          return node;
        }

        static formats(node: HTMLElement): any {
          if (entry.tagName === 'A') {
            const href = node.getAttribute('href');
            const id = node.getAttribute('id');

            if (href) {
              return { href: node.getAttribute('href') };
            }
            if (id) {
              return { id: node.getAttribute('id') };
            }
            return '';
          } else if (entry.tagName === 'SPAN' || entry.tagName === 'BR') {
            return true;
          }

          return undefined;
        }
      }

      this.instance.register(CustomBlot);
    }
  }
}
